"use client";

import { useEffect, useState } from "react";

import { getLegalContent } from "@/content/legal";
import { LEGAL_BASELINE_COUNTRY } from "@/content/legal-country-overlay";
import {
  getLegalPolicySnapshot,
  legalDir,
  loadLegalCountryOverlay,
  loadLegalLocaleCache,
  type LegalPolicyKey,
} from "@/content/legal-policies";
import type { Locale } from "@/lib/i18n/config";
import { readVisitorCountryFromWindow } from "@/lib/store-urls";

type Props = {
  page: LegalPolicyKey;
  locale: Locale;
};

export default function LegalPolicyEmbed({ page, locale }: Props) {
  const legal = getLegalContent(locale);
  const [policy, setPolicy] = useState(() => getLegalPolicySnapshot(page, locale));
  const [country, setCountry] = useState(LEGAL_BASELINE_COUNTRY);

  useEffect(() => {
    let cancelled = false;
    const visitorCountry = readVisitorCountryFromWindow();
    void (async () => {
      await loadLegalLocaleCache(locale);
      const overlay = await loadLegalCountryOverlay(visitorCountry);
      if (cancelled) return;
      setCountry(overlay?.country ?? LEGAL_BASELINE_COUNTRY);
      setPolicy(getLegalPolicySnapshot(page, locale, overlay));
    })();
    return () => {
      cancelled = true;
    };
  }, [page, locale]);

  return (
    <section
      className="legal-cached-policy-shell"
      data-legal-policy-page={page}
      data-legal-locale={locale}
      data-legal-resolved-locale={policy.resolvedLocale}
      data-legal-cache-sha={policy.contentSha256}
      data-legal-country={country}
      data-legal-overlay="static"
      dir={legalDir(locale)}
    >
      {policy.resolvedLocale !== locale ? (
        <p className="legal-cached-policy-notice">
          {legal.ui.translationFallbackNotice}
        </p>
      ) : null}

      <div
        className="legal-cached-policy"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: policy.content }}
      />
    </section>
  );
}
